import { type ContentStatus, contentLocaleSchema, contentStatusSchema } from "@content-types"
import * as z from "zod"
import {
  type ContentLanguageFilter,
  type ContentTeamFilter,
  DASHBOARD_CONTENT_LIST_PAGE_SIZE_OPTIONS,
  DASHBOARD_CONTENT_LIST_PER_PAGE,
} from "./content-queries"

/**
 * The content list's filters, as URL state.
 *
 * These lived in Jotai atoms, which made them invisible to the server: a route
 * loader cannot prefetch a list it cannot describe, so the table could only
 * start loading once the browser had mounted the app and the atoms had told it
 * what to ask for. Putting them in the URL is what lets the loader resolve the
 * data alongside the document.
 *
 * It also fixes three things that were never really acceptable: a filtered
 * view could not be linked or bookmarked, the back button stepped out of the
 * section instead of back through the filters, and a reload silently dropped
 * you back to published-page-one.
 */
/** The page sizes the pagination control offers. */
export type ContentListPageSize = (typeof DASHBOARD_CONTENT_LIST_PAGE_SIZE_OPTIONS)[number]

export type ContentListSearch = {
  status: ContentStatus
  language: ContentLanguageFilter
  team: ContentTeamFilter
  query: string
  page: number
  pageSize: ContentListPageSize
}

export const CONTENT_LIST_SEARCH_DEFAULTS: ContentListSearch = {
  status: "published",
  language: "all",
  team: "all",
  query: "",
  page: 1,
  pageSize: DASHBOARD_CONTENT_LIST_PER_PAGE,
}

/**
 * Every field optional, deliberately.
 *
 * A default that survives validation gets written back into the URL, so
 * landing on a section would rewrite the address bar with six parameters that
 * say nothing. Parsing leaves them absent and `contentListSearchFrom` fills
 * them in, which keeps `/dashboard/news` looking like `/dashboard/news`.
 */
export const contentListSearchSchema = z.object({
  status: contentStatusSchema.optional(),
  language: z.union([z.literal("all"), contentLocaleSchema]).optional(),
  team: z.string().trim().min(1).optional(),
  query: z.string().optional(),
  page: z.coerce.number().int().positive().optional(),
  pageSize: z.union(DASHBOARD_CONTENT_LIST_PAGE_SIZE_OPTIONS.map((size) => z.literal(size))).optional(),
})

export type ContentListSearchInput = z.infer<typeof contentListSearchSchema>

/**
 * Validates a route's raw search object, dropping anything unusable.
 *
 * A hand-edited or stale URL must not be able to break the page, so this never
 * throws: a parameter that fails validation is simply absent, and the reader
 * below substitutes its default.
 */
export function validateContentListSearch(search: Record<string, unknown>): ContentListSearchInput {
  const parsed = contentListSearchSchema.safeParse(search)
  return parsed.success ? parsed.data : {}
}

/**
 * Narrows an arbitrary number to a page size the control actually offers.
 *
 * The pagination component hands back a plain number, and a URL can carry
 * anything at all; both funnel through here so an unsupported size lands on
 * the default rather than being passed to the server.
 */
export function asContentListPageSize(value: number): ContentListPageSize {
  return DASHBOARD_CONTENT_LIST_PAGE_SIZE_OPTIONS.find((size) => size === value)
    ? (value as ContentListPageSize)
    : CONTENT_LIST_SEARCH_DEFAULTS.pageSize
}

/** Fills a validated search object out into complete list state. */
export function contentListSearchFrom(search: ContentListSearchInput | undefined): ContentListSearch {
  return {
    status: search?.status ?? CONTENT_LIST_SEARCH_DEFAULTS.status,
    language: search?.language ?? CONTENT_LIST_SEARCH_DEFAULTS.language,
    team: search?.team ?? CONTENT_LIST_SEARCH_DEFAULTS.team,
    query: search?.query ?? CONTENT_LIST_SEARCH_DEFAULTS.query,
    page: search?.page ?? CONTENT_LIST_SEARCH_DEFAULTS.page,
    pageSize: search?.pageSize ?? CONTENT_LIST_SEARCH_DEFAULTS.pageSize,
  }
}

/**
 * The inverse: state back to the smallest search object that describes it.
 *
 * Anything sitting at its default is omitted rather than serialized, so the
 * URL carries only what the reader actually changed.
 */
export function contentListSearchToParams(state: ContentListSearch): ContentListSearchInput {
  const params: ContentListSearchInput = {}
  if (state.status !== CONTENT_LIST_SEARCH_DEFAULTS.status) params.status = state.status
  if (state.language !== CONTENT_LIST_SEARCH_DEFAULTS.language) params.language = state.language
  if (state.team !== CONTENT_LIST_SEARCH_DEFAULTS.team) params.team = state.team
  if (state.query !== CONTENT_LIST_SEARCH_DEFAULTS.query) params.query = state.query
  if (state.page !== CONTENT_LIST_SEARCH_DEFAULTS.page) params.page = state.page
  if (state.pageSize !== CONTENT_LIST_SEARCH_DEFAULTS.pageSize) params.pageSize = state.pageSize
  return params
}

/**
 * Applies a change the way the filter controls do.
 *
 * Narrowing a list while standing on page nine used to leave you on page nine
 * of a result set that no longer had one, so every criterion change resets
 * paging. Paging itself is the one change that must not.
 */
export function nextContentListSearch(
  current: ContentListSearch,
  change: Partial<ContentListSearch>
): ContentListSearch {
  const next = { ...current, ...change }
  const onlyPageChanged = Object.keys(change).every((key) => key === "page")
  return onlyPageChanged ? next : { ...next, page: change.page ?? 1 }
}

export type { ContentLanguageFilter, ContentTeamFilter }
